import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Download } from 'lucide-react';
import { statusLabel, levelLabel } from './conductUtils';

function StatBlock({ title, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function CountList({ entries, labelFn, emptyText, testIdPrefix }) {
  const rows = Object.entries(entries || {});
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm" data-testid={`${testIdPrefix}-${k}`}>
          <span>{labelFn ? labelFn(k) : k}</span>
          <span className="font-medium">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/conduct/dashboard')).data); }
    catch (e) { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/conduct/export/restrictions.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'conduct_restrictions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error('Failed to export restrictions'); }
    finally { setExporting(false); }
  };

  if (loading || !data) return <div className="flex justify-center py-12" data-testid="dashboard-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="conduct-dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatBlock title="Open Cases by Status" testId="dashboard-open-cases">
          <CountList entries={data.open_cases_by_status} labelFn={(s) => statusLabel(s, null, 'hr')} emptyText="No open cases." testIdPrefix="dashboard-open-case" />
        </StatBlock>
        <StatBlock title="Active Cards by Level" testId="dashboard-active-by-level">
          <CountList entries={data.active_cards_by_level} labelFn={(l) => levelLabel(l)} emptyText="No active cards." testIdPrefix="dashboard-active-level" />
        </StatBlock>
        <StatBlock title="Active Cards by Team" testId="dashboard-active-by-team">
          <CountList entries={data.active_cards_by_team} emptyText="No active cards." testIdPrefix="dashboard-active-team" />
        </StatBlock>
        <StatBlock title="Reports and Dismissals by Manager" testId="dashboard-by-manager">
          <p className="text-xs font-medium text-muted-foreground mb-1">Reports filed</p>
          <CountList entries={data.reports_by_manager} emptyText="No reports yet." testIdPrefix="dashboard-reports" />
          <p className="text-xs font-medium text-muted-foreground mt-3 mb-1">Dismissals</p>
          <CountList entries={data.dismissals_by_manager} emptyText="No dismissals yet." testIdPrefix="dashboard-dismissals" />
        </StatBlock>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatBlock title="Reviews Due This Week" testId="dashboard-reviews-due">
          <p className="text-3xl font-semibold" data-testid="dashboard-reviews-due-count">{data.reviews_due_this_week}</p>
        </StatBlock>
        <StatBlock title="Cards Clearing in the Next 7 Days" testId="dashboard-clearing-soon">
          <p className="text-3xl font-semibold" data-testid="dashboard-clearing-soon-count">{data.cards_clearing_in_7_days}</p>
        </StatBlock>
      </div>

      {user?.export_allowed && (
        <Button variant="outline" disabled={exporting} data-testid="dashboard-export-csv-btn" onClick={exportCsv}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Download Restrictions (CSV)
        </Button>
      )}
    </div>
  );
}
